import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, FileText, BookOpen, FileSpreadsheet, BarChart3, ArrowRight, Mail, X, Loader2, Settings } from 'lucide-react';
import { resourcesData } from '../mock/data';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const categoryIcons = {
  'Whitepapers': FileText,
  'Guides': BookOpen,
  'Templates': FileSpreadsheet,
  'Research Reports': BarChart3,
  'eBooks': BookOpen,
  'Checklists': FileSpreadsheet,
  'Toolkits': FileSpreadsheet,
  'Case Studies': BarChart3
};

const Resources = ({ onBackToHome, onAdminClick, onResourceClick }) => {
  const { t, i18n } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch resources from API
  useEffect(() => {
    const fetchResources = async () => {
      try {
        const lang = i18n.language || 'en';
        const response = await fetch(apiUrl(`resources?lang=${lang}`));
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setResources(data);
            // Extract unique categories
            const uniqueCategories = ['All', ...new Set(data.map(r => r.category))];
            setCategories(uniqueCategories);
          } else {
            // Fallback to mock data if no resources in DB
            setResources(resourcesData.resources);
            setCategories(resourcesData.categories);
          }
        } else {
          // Fallback to mock data
          setResources(resourcesData.resources);
          setCategories(resourcesData.categories);
        }
      } catch (error) {
        logger.error('Error fetching resources:', error);
        // Fallback to mock data
        setResources(resourcesData.resources);
        setCategories(resourcesData.categories);
      }
      setIsLoading(false);
    };
    
    fetchResources();
  }, [i18n.language]);

  const filteredResources = selectedCategory === 'All'
    ? resources
    : resources.filter(r => r.category === selectedCategory);

  const featuredResources = resources.filter(r => r.featured);

  const handleResourceOpen = (resource) => {
    if (onResourceClick) {
      onResourceClick(resource.id);
    } else {
      // Fallback to legacy modal if parent didn't wire navigation
      setSelectedResource(resource);
      setShowDownloadModal(true);
    }
  };

  const handleDownloadClick = (resource) => {
    setSelectedResource(resource);
    setShowDownloadModal(true);
  };

  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // If resource has a direct fileUrl from DB, use it
      if (selectedResource.fileUrl && selectedResource.fileUrl.includes('/api/resources/')) {
        // Submit download request for tracking
        await fetch(apiUrl("resource-downloads"), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fullName: name,
            email: email,
            company: '',
            resourceId: selectedResource.id,
            resourceTitle: selectedResource.title
          })
        });

        toast.success(i18n.language === 'tr' ? 'İndirme başlatılıyor...' : 'Starting download...');
        setShowDownloadModal(false);
        
        // Trigger direct download
        const link = document.createElement('a');
        link.href = selectedResource.fileUrl;
        link.download = selectedResource.title + '.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setEmail('');
        setName('');
      } else {
        // Fallback for mock data - use the old endpoint
        const response = await fetch(apiUrl("resource-downloads"), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fullName: name,
            email: email,
            company: '',
            resourceId: selectedResource.id,
            resourceTitle: selectedResource.title
          })
        });

        if (response.ok) {
          const data = await response.json();
          toast.success(i18n.language === 'tr' ? 'İndirme başlatılıyor...' : 'Starting download...');
          setShowDownloadModal(false);
          
          const link = document.createElement('a');
          link.href = `${API_URL}${data.downloadUrl}`;
          link.download = `${selectedResource.id}.pdf`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setEmail('');
          setName('');
        } else {
          throw new Error('Download failed');
        }
      }
    } catch (error) {
      logger.error('Error:', error);
      toast.error(i18n.language === 'tr' ? 'Bir hata oluştu. Lütfen tekrar deneyin.' : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={onBackToHome}
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {i18n.language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
          {onAdminClick && (
            <button
              onClick={onAdminClick}
              className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
            >
              <Settings className="w-4 h-4 mr-2" />
              Admin Panel
            </button>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="container mx-auto px-6 text-center">
          <p className="text-sm text-blue-400 uppercase tracking-wider mb-4 font-semibold">{t('nav.resources')}</p>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('pages.resources.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('pages.resources.subtitle')}
          </p>
        </div>
      </div>

      {/* Featured Resources */}
      <div className="container mx-auto px-6 -mt-12 relative z-10">
        <div className="grid md:grid-cols-3 gap-6">
          {featuredResources.slice(0, 3).map((resource) => {
            const Icon = categoryIcons[resource.category] || FileText;
            return (
              <div 
                key={resource.id}
                onClick={() => handleResourceOpen(resource)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResourceOpen(resource); }}
                data-testid={`resource-card-featured-${resource.id}`}
                className="bg-white rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer"
              >
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={resource.image}
                    alt={resource.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                      {t('pages.blog.featured')}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-600 font-medium">{resource.category}</span>
                    <span className="text-sm text-gray-400">• {resource.pages} {t('pages.resources.pages')}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {resource.description}
                  </p>
                  <Button 
                    onClick={(e) => { e.stopPropagation(); handleResourceOpen(resource); }}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {i18n.language === 'tr' ? 'Ücretsiz İndir' : 'Download Free'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Category Filter */}
      <div className="container mx-auto px-6 py-12">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-3 justify-center mb-12">
              {categories.map((category) => {
                const Icon = categoryIcons[category];
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-5 py-2 rounded-full font-medium transition-all duration-300 flex items-center gap-2 ${
                      selectedCategory === category
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    {category === 'All' ? (i18n.language === 'tr' ? 'Tümü' : 'All') : category}
                  </button>
                );
              })}
            </div>

        {/* Resources Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredResources.map((resource) => {
            const Icon = categoryIcons[resource.category] || FileText;
            return (
              <div 
                key={resource.id}
                onClick={() => handleResourceOpen(resource)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResourceOpen(resource); }}
                data-testid={`resource-card-${resource.id}`}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 group cursor-pointer"
              >
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={resource.image}
                    alt={resource.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-600 font-medium">{resource.category}</span>
                    <span className="text-sm text-gray-400">• {resource.pages} {t('pages.resources.pages')}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {resource.description}
                  </p>
                  <span
                    className="text-blue-600 font-medium text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    {t('pages.resources.download')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
          </>
        )}
      </div>

      {/* Download Modal */}
      {showDownloadModal && selectedResource && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {i18n.language === 'tr' ? 'Kaynağı İndir' : 'Download Resource'}
                </h3>
                <button 
                  onClick={() => setShowDownloadModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <img 
                  src={selectedResource.image}
                  alt={selectedResource.title}
                  className="w-20 h-16 object-cover rounded-lg"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">{selectedResource.title}</h4>
                  <p className="text-sm text-gray-500">{selectedResource.category} • {selectedResource.pages} {t('pages.resources.pages')}</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                {i18n.language === 'tr' ? 'Bu ücretsiz kaynağı indirmek için bilgilerinizi girin. E-postanıza da bir kopya göndereceğiz.' : 'Enter your details to download this free resource. We\'ll also send a copy to your email.'}
              </p>

              <form onSubmit={handleDownloadSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('contact.form.fullName')}
                  </label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder={i18n.language === 'tr' ? 'Adınız' : 'Your name'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('contact.form.email')}
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@company.com"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {i18n.language === 'tr' ? 'İşleniyor...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      {i18n.language === 'tr' ? 'Şimdi İndir' : 'Download Now'}
                    </>
                  )}
                </Button>
              </form>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                {i18n.language === 'tr' ? 'İndirerek, CoachMeUpp\'tan ara sıra e-posta almayı kabul etmiş olursunuz. İstediğiniz zaman abonelikten çıkabilirsiniz.' : 'By downloading, you agree to receive occasional emails from CoachMeUpp. You can unsubscribe anytime.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="bg-gray-900 py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {i18n.language === 'tr' ? 'Özel Bir Çözüme mi İhtiyacınız Var?' : 'Need a Custom Solution?'}
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            {i18n.language === 'tr' ? 'Ekibimiz organizasyonunuz için özelleştirilmiş kaynaklar ve koçluk programları oluşturabilir' : 'Our team can create tailored resources and coaching programs for your organization'}
          </p>
          <Button 
            onClick={() => {
              onBackToHome();
              setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100);
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg"
          >
            {i18n.language === 'tr' ? 'Ekibimizle İletişime Geçin' : 'Contact Our Team'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Resources;
